import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { MARKET_MONITOR_TOOLTIP_PROPS } from "./tooltipConfig";

interface NetLoadPoint {
  index: number;
  timestampIso: string;
  timestampLabel: string;
  dateLabel: string;
  curtailment: number;
  renewableGenRealTime: number | null;
  renewableGenForecast: number | null;
  solarGenRealTime: number | null;
  solarGenForecast: number | null;
  windGenRealTime: number | null;
  windGenForecast: number | null;
  netLoadForecast: number | null;
  netLoadRealTime: number | null;
  netLoadRealTimeWithCurtailment: number | null;
  loadForecast: number | null;
  loadRealTime: number | null;
}

interface MarketDataRow {
  ISO?: string;
  OBJECTNAME?: string | null;
  DATATYPEID?: number | string | null;
  TIMESTAMP?: string;
  DATETIME?: string;
  DATA_POINTS?: string;
  SHORTNAME?: string;
  VALUE?: string | number | null;
  VOLUME?: string | number | null;
}

interface DatatypeRow {
  DATATYPEID?: number | string | null;
  GENERATIONTYPE?: string | null;
}

interface NetLoadVisualProps {
  loadForecastRows: MarketDataRow[];
  renewableForecastRows: MarketDataRow[];
  realtimeLoadRows: MarketDataRow[];
  renewableRealtimeRows: MarketDataRow[];
  curtailmentRows: MarketDataRow[];
  datatypesRows: DatatypeRow[];
  selectedIso: string;
}

interface SpreadPoint extends NetLoadPoint {
  netLoadSpread: number | null;
  netLoadCurtailmentSpread: number | null;
}

interface SolarWindSpreadPoint {
  index: number;
  timestampIso: string;
  timestampLabel: string;
  dateLabel: string;
  solarSpread: number;
  windSpread: number;
}

type GenerationTypeSelection = "ALL" | "SOLAR" | "WIND";

const generationTabBaseClass =
  "w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base rounded-[10px] transition-all text-center";

const getGenerationTabClassName = (isActive: boolean): string =>
  `${generationTabBaseClass} ${
    isActive
      ? "bg-white text-[#FF2C2C] font-bold shadow-[0_6px_15px_rgba(0,0,0,0.15)] border-t-[2px] border-l-[2px] border-r-[2px] border-[#EAE7FF]"
      : "bg-[#e3e5f2] text-gray-600"
  }`;

const MAX_POINTS = 14 * 24;
const EXCLUDED_OBJECT_NAMES = new Set(["SP15", "ZP26", "NP15"]);
const EXCLUDED_GENERATION_TYPES = new Set(["BIO", "HYBRID"]);
const DIMMED_COLOR = "#cbd5e1";
const PJM_COMBINED_OBJECT_NAME = "RTO COMBINED";

const ISO_ALIASES: Record<string, string[]> = {
  CAISO: ["CAISO"],
  ERCOT: ["ERCOT"],
  ISONE: ["ISONE", "NEISO"],
  NYISO: ["NYISO"],
  PJM: ["PJM", "PJMISO"],
};

const toggleSeriesSelection = (
  seriesKey: string,
  setSelectedSeriesKey: React.Dispatch<React.SetStateAction<string | null>>
) => {
  setSelectedSeriesKey((previous) => (previous === seriesKey ? null : seriesKey));
};

const getSeriesColor = (
  seriesKey: string,
  baseColor: string,
  selectedSeriesKey: string | null
): string => (selectedSeriesKey && selectedSeriesKey !== seriesKey ? DIMMED_COLOR : baseColor);

const getSeriesOpacity = (seriesKey: string, selectedSeriesKey: string | null): number =>
  selectedSeriesKey && selectedSeriesKey !== seriesKey ? 0.45 : 1;

const getLegendSeriesKey = (
  entry: unknown,
  validSeriesKeys: readonly string[],
  labelToSeriesKey: Record<string, string>
): string | null => {
  const legendEntry = entry as { value?: unknown; dataKey?: unknown };
  const dataKey = String(legendEntry?.dataKey ?? "");
  if (validSeriesKeys.includes(dataKey)) {
    return dataKey;
  }

  const value = String(legendEntry?.value ?? "");
  const mapped = labelToSeriesKey[value];
  if (mapped && validSeriesKeys.includes(mapped)) {
    return mapped;
  }

  return null;
};

const renderLegendLabel = (
  value: string,
  selectedSeriesKey: string | null,
  labelToSeriesKey: Record<string, string>
): React.ReactNode => {
  const resolvedKey = labelToSeriesKey[value] ?? value;
  const isDimmed = !!(selectedSeriesKey && selectedSeriesKey !== resolvedKey);

  return (
    <span style={{ color: isDimmed ? "#94a3b8" : "#374151", opacity: isDimmed ? 0.65 : 1 }}>
      {value}
    </span>
  );
};

const toNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toDatatypeKey = (value: number | string | null | undefined): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return String(value);
};

const hasAllowedObjectName = (row: MarketDataRow): boolean => {
  const objectName = row.OBJECTNAME?.trim();
  if (!objectName) {
    return false;
  }

  return !EXCLUDED_OBJECT_NAMES.has(objectName.toUpperCase());
};

const matchesSelectedIso = (rowIso: string | undefined, selectedIso: string): boolean => {
  const rowIsoUpper = rowIso?.trim().toUpperCase();
  const selectedIsoUpper = selectedIso.trim().toUpperCase();

  if (!rowIsoUpper || !selectedIsoUpper) {
    return false;
  }

  if (rowIsoUpper === selectedIsoUpper) {
    return true;
  }

  const allowedIsos = ISO_ALIASES[selectedIsoUpper] ?? [selectedIsoUpper];
  return allowedIsos.includes(rowIsoUpper);
};

const matchesPjmCombinedObject = (row: MarketDataRow, selectedIso: string): boolean => {
  if (selectedIso.trim().toUpperCase() !== "PJM") {
    return true;
  }

  const objectName = row.OBJECTNAME?.trim().toUpperCase();
  return objectName === PJM_COMBINED_OBJECT_NAME;
};

const hasAllowedGenerationType = (
  row: MarketDataRow,
  generationTypeByDatatype: Map<string, string>
): boolean => {
  const datatypeKey = toDatatypeKey(row.DATATYPEID);
  if (!datatypeKey) {
    return false;
  }

  const generationType = generationTypeByDatatype.get(datatypeKey)?.trim();
  if (!generationType) {
    return false;
  }

  return !EXCLUDED_GENERATION_TYPES.has(generationType.toUpperCase());
};

const toHourKey = (value: string | undefined): string | null => {
  if (!value) {
    return null;
  }

  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = new Date(normalized);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  parsed.setMinutes(0, 0, 0);
  return parsed.toISOString();
};

const toShortDate = (isoDateTime: string): string => {
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) {
    return isoDateTime;
  }

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
  });
};

const toFullTimeLabel = (isoDateTime: string): string => {
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) {
    return isoDateTime;
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const roundValue = (value: number): number => Math.round(value * 100) / 100;

const formatYAxis = (value: number): string => `${Math.round(value / 1000)}K`;

const formatCurtailmentYAxis = (value: number): string => {
  if (Math.abs(value) < 1000) {
    return `${Math.round(value)}`;
  }

  const inThousands = value / 1000;
  return `${Number.isInteger(inThousands) ? inThousands.toFixed(0) : inThousands.toFixed(1)}K`;
};

const formatTooltip = (value: number): string =>
  `${value.toLocaleString("en-US", { maximumFractionDigits: 0 })} MW`;

const formatSpreadTooltip = (value: number): string =>
  `${value.toLocaleString("en-US", { maximumFractionDigits: 2 })} MW`;

const getClosedYAxisDomain = (
  points: NetLoadPoint[],
  keys: Array<keyof NetLoadPoint>
): [number, number] => {
  const values: number[] = [];

  points.forEach((point) => {
    keys.forEach((key) => {
      const value = point[key];
      if (typeof value === "number" && Number.isFinite(value)) {
        values.push(value);
      }
    });
  });

  if (!values.length) {
    return [0, 1];
  }

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  if (minValue === maxValue) {
    const padding = Math.max(Math.abs(minValue) * 0.05, 1);
    return [roundValue(minValue - padding), roundValue(maxValue + padding)];
  }

  const range = maxValue - minValue;
  const padding = Math.max(range * 0.06, 100);
  return [roundValue(minValue - padding), roundValue(maxValue + padding)];
};

const getGenerationType = (
  row: MarketDataRow,
  generationTypeByDatatype: Map<string, string>
): string => {
  const datatypeKey = toDatatypeKey(row.DATATYPEID);
  if (!datatypeKey) {
    return "";
  }

  return generationTypeByDatatype.get(datatypeKey)?.trim().toUpperCase() ?? "";
};

const matchesGenerationTypeSelection = (
  row: MarketDataRow,
  generationTypeByDatatype: Map<string, string>,
  selectedGenerationType: GenerationTypeSelection
): boolean => {
  if (selectedGenerationType === "ALL") {
    return true;
  }

  const generationType = getGenerationType(row, generationTypeByDatatype);
  return generationType.includes(selectedGenerationType);
};

const getCurtailmentGenerationType = (
  row: MarketDataRow,
  generationTypeByDatatype: Map<string, string>
): GenerationTypeSelection | null => {
  const generationType = getGenerationType(row, generationTypeByDatatype);
  if (generationType.includes("SOLAR")) {
    return "SOLAR";
  }

  if (generationType.includes("WIND")) {
    return "WIND";
  }

  const shortName = row.SHORTNAME?.toUpperCase() ?? "";
  if (shortName.includes("SOLAR")) {
    return "SOLAR";
  }

  if (shortName.includes("WIND")) {
    return "WIND";
  }

  return null;
};

const matchesCurtailmentSelection = (
  row: MarketDataRow,
  generationTypeByDatatype: Map<string, string>,
  selectedGenerationType: GenerationTypeSelection
): boolean => {
  const curtailmentGenerationType = getCurtailmentGenerationType(row, generationTypeByDatatype);
  if (!curtailmentGenerationType) {
    return false;
  }

  if (selectedGenerationType === "ALL") {
    return true;
  }

  return curtailmentGenerationType === selectedGenerationType;
};

const buildChartData = (
  loadForecastRows: MarketDataRow[],
  renewableForecastRows: MarketDataRow[],
  realtimeLoadRows: MarketDataRow[],
  renewableRealtimeRows: MarketDataRow[],
  curtailmentRows: MarketDataRow[]
): NetLoadPoint[] => {
  const loadForecastByHour = new Map<string, number>();
  const renewableForecastByHour = new Map<string, number>();
  const renewableForecastAllByHour = new Map<string, number>();
  const solarForecastByHour = new Map<string, number>();
  const windForecastByHour = new Map<string, number>();
  const realtimeLoadByHourBucket = new Map<string, { sum: number; count: number }>();
  const renewableRealtimeByHour = new Map<string, number>();
  const renewableRealtimeAllByHour = new Map<string, number>();
  const solarRealtimeByHour = new Map<string, number>();
  const windRealtimeByHour = new Map<string, number>();
  const curtailmentByHour = new Map<string, number>();

  loadForecastRows.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP);
    const value = toNumber(row.VALUE);
    if (!hourKey || value === null) {
      return;
    }
    loadForecastByHour.set(hourKey, value);
  });

  renewableForecastRows.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP);
    const value = toNumber(row.VALUE);
    if (!hourKey || value === null) {
      return;
    }

    const shortName = row.SHORTNAME?.toUpperCase() ?? "";
    if (!shortName.includes("SOLAR") && !shortName.includes("WIND")) {
      return;
    }

    const previous = renewableForecastByHour.get(hourKey) ?? 0;
    renewableForecastByHour.set(hourKey, previous + value);
  });

  renewableForecastRows.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VALUE ?? row.VOLUME);
    if (!hourKey || value === null) {
      return;
    }

    const shortName = row.SHORTNAME?.toUpperCase() ?? "";
    if (!shortName.includes("SOLAR") && !shortName.includes("WIND")) {
      return;
    }

    const previous = renewableForecastAllByHour.get(hourKey) ?? 0;
    renewableForecastAllByHour.set(hourKey, previous + value);

    if (shortName.includes("SOLAR")) {
      solarForecastByHour.set(hourKey, (solarForecastByHour.get(hourKey) ?? 0) + value);
    }

    if (shortName.includes("WIND")) {
      windForecastByHour.set(hourKey, (windForecastByHour.get(hourKey) ?? 0) + value);
    }
  });

  realtimeLoadRows.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VALUE ?? row.VOLUME);
    if (!hourKey || value === null) {
      return;
    }

    const bucket = realtimeLoadByHourBucket.get(hourKey) ?? { sum: 0, count: 0 };
    bucket.sum += value;
    bucket.count += 1;
    realtimeLoadByHourBucket.set(hourKey, bucket);
  });

  renewableRealtimeRows.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VALUE ?? row.VOLUME);
    if (!hourKey || value === null) {
      return;
    }

    const shortName = row.SHORTNAME?.toUpperCase() ?? "";
    if (!shortName.includes("SOLAR") && !shortName.includes("WIND")) {
      return;
    }

    const previous = renewableRealtimeByHour.get(hourKey) ?? 0;
    renewableRealtimeByHour.set(hourKey, previous + value);
  });

  renewableRealtimeRows.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VALUE ?? row.VOLUME);
    if (!hourKey || value === null) {
      return;
    }

    const shortName = row.SHORTNAME?.toUpperCase() ?? "";
    if (!shortName.includes("SOLAR") && !shortName.includes("WIND")) {
      return;
    }

    const previous = renewableRealtimeAllByHour.get(hourKey) ?? 0;
    renewableRealtimeAllByHour.set(hourKey, previous + value);

    if (shortName.includes("SOLAR")) {
      solarRealtimeByHour.set(hourKey, (solarRealtimeByHour.get(hourKey) ?? 0) + value);
    }

    if (shortName.includes("WIND")) {
      windRealtimeByHour.set(hourKey, (windRealtimeByHour.get(hourKey) ?? 0) + value);
    }
  });

  curtailmentRows.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VALUE ?? row.VOLUME);
    if (!hourKey || value === null) {
      return;
    }

    const previous = curtailmentByHour.get(hourKey) ?? 0;
    curtailmentByHour.set(hourKey, previous + value);
  });

  const realtimeLoadByHour = new Map<string, number>();
  realtimeLoadByHourBucket.forEach((bucket, hourKey) => {
    if (bucket.count > 0) {
      realtimeLoadByHour.set(hourKey, bucket.sum / bucket.count);
    }
  });

  const loadForecastTimeline = Array.from(loadForecastByHour.keys()).sort();
  const combinedForecastTimeline = Array.from(
    new Set([...loadForecastByHour.keys(), ...renewableForecastByHour.keys()])
  ).sort();

  // Keep the primary chart window aligned to load-forecast timestamps (Power BI parity).
  const forecastTimeline = (loadForecastTimeline.length > 0
    ? loadForecastTimeline
    : combinedForecastTimeline
  ).slice(-MAX_POINTS);

  return forecastTimeline.map((hourKey, index) => {
    const loadForecast = loadForecastByHour.get(hourKey) ?? null;
    const forecastRenewables = renewableForecastByHour.get(hourKey) ?? null;

    const loadRealTime = realtimeLoadByHour.get(hourKey) ?? null;
    const realtimeRenewables = renewableRealtimeByHour.get(hourKey) ?? null;
    const renewableGenRealTime = renewableRealtimeAllByHour.get(hourKey) ?? null;
    const renewableGenForecast = renewableForecastAllByHour.get(hourKey) ?? null;
    const solarGenRealTime = solarRealtimeByHour.get(hourKey) ?? null;
    const solarGenForecast = solarForecastByHour.get(hourKey) ?? null;
    const windGenRealTime = windRealtimeByHour.get(hourKey) ?? null;
    const windGenForecast = windForecastByHour.get(hourKey) ?? null;
    const curtailment = curtailmentByHour.get(hourKey) ?? 0;

    const netLoadForecast =
      loadForecast !== null && forecastRenewables !== null
        ? roundValue(loadForecast - forecastRenewables)
        : null;

    const netLoadRealTime =
      loadRealTime !== null && realtimeRenewables !== null
        ? roundValue(loadRealTime - realtimeRenewables)
        : null;

    const netLoadRealTimeWithCurtailment =
      netLoadRealTime !== null ? roundValue(netLoadRealTime + curtailment) : null;

    return {
      index,
      timestampIso: hourKey,
      timestampLabel: toFullTimeLabel(hourKey),
      dateLabel: toShortDate(hourKey),
      curtailment: roundValue(curtailment),
      renewableGenRealTime:
        renewableGenRealTime !== null ? roundValue(renewableGenRealTime) : null,
      renewableGenForecast:
        renewableGenForecast !== null ? roundValue(renewableGenForecast) : null,
      solarGenRealTime: solarGenRealTime !== null ? roundValue(solarGenRealTime) : null,
      solarGenForecast: solarGenForecast !== null ? roundValue(solarGenForecast) : null,
      windGenRealTime: windGenRealTime !== null ? roundValue(windGenRealTime) : null,
      windGenForecast: windGenForecast !== null ? roundValue(windGenForecast) : null,
      netLoadForecast,
      netLoadRealTime,
      netLoadRealTimeWithCurtailment,
      loadForecast: loadForecast !== null ? roundValue(loadForecast) : null,
      loadRealTime: loadRealTime !== null ? roundValue(loadRealTime) : null,
    };
  });
};

const buildSolarWindSpreadData = (
  renewableForecastRows: MarketDataRow[],
  renewableRealtimeRows: MarketDataRow[],
  generationTypeByDatatype: Map<string, string>
): SolarWindSpreadPoint[] => {
  const solarForecastByHour = new Map<string, number>();
  const windForecastByHour = new Map<string, number>();
  const solarRealtimeByHour = new Map<string, number>();
  const windRealtimeByHour = new Map<string, number>();

  renewableForecastRows.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VALUE ?? row.VOLUME);
    if (!hourKey || value === null) {
      return;
    }

    const generationType = getGenerationType(row, generationTypeByDatatype);
    if (generationType.includes("SOLAR")) {
      solarForecastByHour.set(hourKey, (solarForecastByHour.get(hourKey) ?? 0) + value);
      return;
    }

    if (generationType.includes("WIND")) {
      windForecastByHour.set(hourKey, (windForecastByHour.get(hourKey) ?? 0) + value);
    }
  });

  renewableRealtimeRows.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VALUE ?? row.VOLUME);
    if (!hourKey || value === null) {
      return;
    }

    const generationType = getGenerationType(row, generationTypeByDatatype);
    if (generationType.includes("SOLAR")) {
      solarRealtimeByHour.set(hourKey, (solarRealtimeByHour.get(hourKey) ?? 0) + value);
      return;
    }

    if (generationType.includes("WIND")) {
      windRealtimeByHour.set(hourKey, (windRealtimeByHour.get(hourKey) ?? 0) + value);
    }
  });

  const timeline = Array.from(
    new Set([
      ...solarForecastByHour.keys(),
      ...windForecastByHour.keys(),
      ...solarRealtimeByHour.keys(),
      ...windRealtimeByHour.keys(),
    ])
  )
    .sort()
    .slice(-MAX_POINTS);

  return timeline.map((hourKey, index) => {
    const solarActual = solarRealtimeByHour.get(hourKey) ?? null;
    const solarForecast = solarForecastByHour.get(hourKey) ?? 0;
    const windActual = windRealtimeByHour.get(hourKey) ?? null;
    const windForecast = windForecastByHour.get(hourKey) ?? 0;

    // Matches the DAX pattern: IF actual is blank then 0 else actual - forecast.
    const solarSpread = solarActual !== null ? roundValue(solarActual - solarForecast) : 0;
    const windSpread = windActual !== null ? roundValue(windActual - windForecast) : 0;

    return {
      index,
      timestampIso: hourKey,
      timestampLabel: toFullTimeLabel(hourKey),
      dateLabel: toShortDate(hourKey),
      solarSpread,
      windSpread,
    };
  });
};

const NetLoadVisual: React.FC<NetLoadVisualProps> = ({
  loadForecastRows,
  renewableForecastRows,
  realtimeLoadRows,
  renewableRealtimeRows,
  curtailmentRows,
  datatypesRows,
  selectedIso,
}) => {
  const [selectedGenerationType, setSelectedGenerationType] =
    useState<GenerationTypeSelection>("ALL");
  const [selectedMainSeriesKey, setSelectedMainSeriesKey] = useState<string | null>(null);
  const [selectedNetSpreadSeriesKey, setSelectedNetSpreadSeriesKey] = useState<string | null>(null);
  const [selectedAggregatedSeriesKey, setSelectedAggregatedSeriesKey] = useState<string | null>(null);
  const [selectedSolarWindSpreadSeriesKey, setSelectedSolarWindSpreadSeriesKey] =
    useState<string | null>(null);

  const generationTypeByDatatype = useMemo(() => {
    const map = new Map<string, string>();
    datatypesRows.forEach((row) => {
      const datatypeKey = toDatatypeKey(row.DATATYPEID);
      const generationType = row.GENERATIONTYPE?.trim();

      if (datatypeKey && generationType) {
        map.set(datatypeKey, generationType);
      }
    });

    return map;
  }, [datatypesRows]);

  const filteredLoadForecastRows = useMemo(
    () =>
      loadForecastRows.filter(
        (row) =>
          matchesSelectedIso(row.ISO, selectedIso) &&
          matchesPjmCombinedObject(row, selectedIso) &&
          row.DATA_POINTS === "LOAD_FORECAST" &&
          hasAllowedObjectName(row)
      ),
    [loadForecastRows, selectedIso]
  );

  const filteredRenewableForecastRows = useMemo(
    () =>
      renewableForecastRows.filter(
        (row) =>
          matchesSelectedIso(row.ISO, selectedIso) &&
          hasAllowedObjectName(row) &&
          hasAllowedGenerationType(row, generationTypeByDatatype) &&
          matchesGenerationTypeSelection(row, generationTypeByDatatype, selectedGenerationType)
      ),
    [renewableForecastRows, generationTypeByDatatype, selectedGenerationType, selectedIso]
  );

  const filteredRealtimeLoadRows = useMemo(
    () =>
      realtimeLoadRows.filter((row) => {
        if (!matchesSelectedIso(row.ISO, selectedIso)) {
          return false;
        }

        if (!matchesPjmCombinedObject(row, selectedIso)) {
          return false;
        }

        if (!hasAllowedObjectName(row)) {
          return false;
        }

        if (!row.DATA_POINTS) {
          return true;
        }

        return row.DATA_POINTS === "ACTUAL_LOAD";
      }),
    [realtimeLoadRows, selectedIso]
  );

  const filteredRenewableRealtimeRows = useMemo(
    () =>
      renewableRealtimeRows.filter(
        (row) =>
          matchesSelectedIso(row.ISO, selectedIso) &&
          hasAllowedObjectName(row) &&
          hasAllowedGenerationType(row, generationTypeByDatatype) &&
          matchesGenerationTypeSelection(row, generationTypeByDatatype, selectedGenerationType)
      ),
    [renewableRealtimeRows, generationTypeByDatatype, selectedGenerationType, selectedIso]
  );

  const filteredCurtailmentRows = useMemo(
    () =>
      curtailmentRows.filter(
        (row) =>
          matchesSelectedIso(row.ISO, selectedIso) &&
          hasAllowedObjectName(row) &&
          matchesCurtailmentSelection(row, generationTypeByDatatype, selectedGenerationType)
      ),
    [curtailmentRows, generationTypeByDatatype, selectedGenerationType, selectedIso]
  );

  const data = useMemo(
    () =>
      buildChartData(
        filteredLoadForecastRows,
        filteredRenewableForecastRows,
        filteredRealtimeLoadRows,
        filteredRenewableRealtimeRows,
        filteredCurtailmentRows
      ),
    [
      filteredLoadForecastRows,
      filteredRenewableForecastRows,
      filteredRealtimeLoadRows,
      filteredRenewableRealtimeRows,
      filteredCurtailmentRows,
    ]
  );

  const solarWindSpreadData = useMemo(
    () =>
      buildSolarWindSpreadData(
        filteredRenewableForecastRows,
        filteredRenewableRealtimeRows,
        generationTypeByDatatype
      ),
    [filteredRenewableForecastRows, filteredRenewableRealtimeRows, generationTypeByDatatype]
  );

  const handleGenerationTypeSelect = (selection: GenerationTypeSelection) => {
    setSelectedGenerationType((previous) => (previous === selection ? "ALL" : selection));
  };

  useEffect(() => {
    setSelectedSolarWindSpreadSeriesKey(null);
    setSelectedAggregatedSeriesKey(null);
  }, [selectedGenerationType]);

  const mainSeriesLabelToKey: Record<string, string> = {
    "Net Load Forecast": "netLoadForecast",
    "Net Load Real Time": "netLoadRealTime",
    "Net Load Real Time w Curtailment": "netLoadRealTimeWithCurtailment",
    "Load Forecast": "loadForecast",
    "Load Real Time": "loadRealTime",
  };

  const mainSeriesKeys = Object.values(mainSeriesLabelToKey);

  const handleMainLegendClick = (entry: unknown) => {
    const key = getLegendSeriesKey(entry, mainSeriesKeys, mainSeriesLabelToKey);
    if (key) {
      toggleSeriesSelection(key, setSelectedMainSeriesKey);
    }
  };

  const mainChartYAxisDomain = useMemo<[number, number]>(
    () =>
      getClosedYAxisDomain(data, [
        "netLoadForecast",
        "netLoadRealTime",
        "netLoadRealTimeWithCurtailment",
        "loadForecast",
        "loadRealTime",
      ]),
    [data]
  );

  const netSpreadSeriesLabelToKey: Record<string, string> = {
    "Net Load Spread": "netLoadSpread",
    "Net Load w/ Curtailment Spread": "netLoadCurtailmentSpread",
  };

  const netSpreadSeriesKeys = Object.values(netSpreadSeriesLabelToKey);

  const handleNetSpreadLegendClick = (entry: unknown) => {
    const key = getLegendSeriesKey(entry, netSpreadSeriesKeys, netSpreadSeriesLabelToKey);
    if (key) {
      toggleSeriesSelection(key, setSelectedNetSpreadSeriesKey);
    }
  };

  const xTicks = useMemo(
    () =>
      data
        .filter((item) => item.index % 48 === 0 || item.index === data.length - 1)
        .map((item) => item.index),
    [data]
  );

  const spreadData = useMemo<SpreadPoint[]>(
    () =>
      data.map((point) => {
        const netLoadSpread =
          point.netLoadForecast !== null
            ? point.netLoadRealTime !== null
              ? roundValue(point.netLoadRealTime - point.netLoadForecast)
              : 0
            : null;

        const netLoadCurtailmentSpread =
          point.netLoadForecast !== null
            ? point.netLoadRealTimeWithCurtailment !== null
              ? roundValue(point.netLoadRealTimeWithCurtailment - point.netLoadForecast)
              : 0
            : null;

        return {
          ...point,
          netLoadSpread,
          netLoadCurtailmentSpread,
        };
      }),
    [data]
  );

  const solarWindSpreadTicks = useMemo(
    () =>
      solarWindSpreadData
        .filter((item) => item.index % 48 === 0 || item.index === solarWindSpreadData.length - 1)
        .map((item) => item.index),
    [solarWindSpreadData]
  );

  const selectedGenerationLabel =
    selectedGenerationType === "ALL"
      ? "Solar + Wind"
      : selectedGenerationType === "SOLAR"
      ? "Solar"
      : "Wind";

  const aggregatedSeriesLabelToKey: Record<string, string> = {
    "Solar Gen Real Time": "solarGenRealTime",
    "Solar Gen Forecast": "solarGenForecast",
    "Wind Gen Real Time": "windGenRealTime",
    "Wind Gen Forecast": "windGenForecast",
  };
  const aggregatedSeriesKeys = Object.values(aggregatedSeriesLabelToKey);

  const handleAggregatedLegendClick = (entry: unknown) => {
    const key = getLegendSeriesKey(entry, aggregatedSeriesKeys, aggregatedSeriesLabelToKey);
    if (key) {
      toggleSeriesSelection(key, setSelectedAggregatedSeriesKey);
    }
  };

  const solarWindSpreadSeriesLabelToKey: Record<string, string> = {
    Solar: "solarSpread",
    Wind: "windSpread",
  };
  const solarWindSpreadSeriesKeys = Object.values(solarWindSpreadSeriesLabelToKey);

  const handleSolarWindSpreadLegendClick = (entry: unknown) => {
    const key = getLegendSeriesKey(
      entry,
      solarWindSpreadSeriesKeys,
      solarWindSpreadSeriesLabelToKey
    );
    if (key) {
      toggleSeriesSelection(key, setSelectedSolarWindSpreadSeriesKey);
    }
  };

  if (!data.length) {
    return (
      <div className="w-full h-[560px] rounded-xl border border-gray-200 bg-white p-4 flex items-center justify-center text-gray-500">
        No data available.
      </div>
    );
  }

  return (
    <div className="w-full grid grid-cols-1 xl:grid-cols-2 gap-5 xl:h-[1240px]">
      <div className="space-y-5 xl:space-y-0 xl:flex xl:flex-col xl:gap-5 xl:h-full">
        <div className="w-full h-[560px] xl:h-auto xl:flex-[56] rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="text-lg font-semibold text-gray-700 mb-2">{selectedIso}</h2>

          <ResponsiveContainer width="100%" height="92%">
            <LineChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
              <XAxis
                dataKey="index"
                tickFormatter={(value) => data[value]?.dateLabel ?? ""}
                ticks={xTicks}
                tick={{ fill: "#6b7280", fontSize: 12 }}
              />
              <YAxis
                domain={mainChartYAxisDomain}
                tickFormatter={formatYAxis}
                tick={{ fill: "#6b7280", fontSize: 12 }}
                label={{ value: "MW", angle: -90, position: "insideLeft", offset: 0 }}
              />
              <Tooltip
                {...MARKET_MONITOR_TOOLTIP_PROPS}
                formatter={(value: number) => formatTooltip(value)}
                labelFormatter={(value) => data[value]?.timestampLabel ?? ""}
              />
              <Legend
                wrapperStyle={{ paddingBottom: 10, cursor: "pointer" }}
                onClick={handleMainLegendClick}
                formatter={(value: string) =>
                  renderLegendLabel(value, selectedMainSeriesKey, mainSeriesLabelToKey)
                }
              />

              <Line
                type="monotone"
                dataKey="netLoadForecast"
                name="Net Load Forecast"
                stroke={getSeriesColor("netLoadForecast", "#1f3a8a", selectedMainSeriesKey)}
                strokeOpacity={getSeriesOpacity("netLoadForecast", selectedMainSeriesKey)}
                strokeWidth={selectedMainSeriesKey === "netLoadForecast" ? 3.2 : 2.2}
                dot={false}
                onClick={() => toggleSeriesSelection("netLoadForecast", setSelectedMainSeriesKey)}
              />
              <Line
                type="monotone"
                dataKey="netLoadRealTime"
                name="Net Load Real Time"
                stroke={getSeriesColor("netLoadRealTime", "#2563eb", selectedMainSeriesKey)}
                strokeOpacity={getSeriesOpacity("netLoadRealTime", selectedMainSeriesKey)}
                strokeWidth={selectedMainSeriesKey === "netLoadRealTime" ? 3.2 : 2.2}
                dot={false}
                onClick={() => toggleSeriesSelection("netLoadRealTime", setSelectedMainSeriesKey)}
              />
              <Line
                type="monotone"
                dataKey="netLoadRealTimeWithCurtailment"
                name="Net Load Real Time w Curtailment"
                stroke={getSeriesColor(
                  "netLoadRealTimeWithCurtailment",
                  "#ea580c",
                  selectedMainSeriesKey
                )}
                strokeOpacity={getSeriesOpacity("netLoadRealTimeWithCurtailment", selectedMainSeriesKey)}
                strokeWidth={selectedMainSeriesKey === "netLoadRealTimeWithCurtailment" ? 3.2 : 2.2}
                dot={false}
                onClick={() =>
                  toggleSeriesSelection("netLoadRealTimeWithCurtailment", setSelectedMainSeriesKey)
                }
              />
              <Line
                type="monotone"
                dataKey="loadForecast"
                name="Load Forecast"
                stroke={getSeriesColor("loadForecast", "#6b21a8", selectedMainSeriesKey)}
                strokeOpacity={getSeriesOpacity("loadForecast", selectedMainSeriesKey)}
                strokeWidth={selectedMainSeriesKey === "loadForecast" ? 3.2 : 2.2}
                dot={false}
                onClick={() => toggleSeriesSelection("loadForecast", setSelectedMainSeriesKey)}
              />
              <Line
                type="monotone"
                dataKey="loadRealTime"
                name="Load Real Time"
                stroke={getSeriesColor("loadRealTime", "#db2777", selectedMainSeriesKey)}
                strokeOpacity={getSeriesOpacity("loadRealTime", selectedMainSeriesKey)}
                strokeWidth={selectedMainSeriesKey === "loadRealTime" ? 3.2 : 2.2}
                dot={false}
                onClick={() => toggleSeriesSelection("loadRealTime", setSelectedMainSeriesKey)}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="w-full h-[340px] xl:h-auto xl:flex-[34] rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-base font-semibold text-gray-700 mb-2">Net Load Spread</h3>
          <ResponsiveContainer width="100%" height="90%">
            <BarChart data={spreadData} margin={{ top: 8, right: 20, left: 10, bottom: 8 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
              <XAxis
                dataKey="index"
                tickFormatter={(value) => spreadData[value]?.dateLabel ?? ""}
                ticks={xTicks}
                tick={{ fill: "#6b7280", fontSize: 12 }}
              />
              <YAxis
                tickFormatter={formatCurtailmentYAxis}
                tick={{ fill: "#6b7280", fontSize: 12 }}
                label={{ value: "MW", angle: -90, position: "insideLeft", offset: 0 }}
              />
              <ReferenceLine y={0} stroke="#9ca3af" />
              <Tooltip
                {...MARKET_MONITOR_TOOLTIP_PROPS}
                formatter={(value: number) => formatSpreadTooltip(value)}
                labelFormatter={(value) => spreadData[value]?.timestampLabel ?? ""}
              />
              <Legend
                wrapperStyle={{ cursor: "pointer" }}
                onClick={handleNetSpreadLegendClick}
                formatter={(value: string) =>
                  renderLegendLabel(value, selectedNetSpreadSeriesKey, netSpreadSeriesLabelToKey)
                }
              />
              <Bar
                dataKey="netLoadSpread"
                name="Net Load Spread"
                fill={getSeriesColor("netLoadSpread", "#1f77d0", selectedNetSpreadSeriesKey)}
                fillOpacity={getSeriesOpacity("netLoadSpread", selectedNetSpreadSeriesKey)}
                barSize={4}
                onClick={() => toggleSeriesSelection("netLoadSpread", setSelectedNetSpreadSeriesKey)}
              />
              <Bar
                dataKey="netLoadCurtailmentSpread"
                name="Net Load w/ Curtailment Spread"
                fill={getSeriesColor(
                  "netLoadCurtailmentSpread",
                  "#e47a33",
                  selectedNetSpreadSeriesKey
                )}
                fillOpacity={getSeriesOpacity(
                  "netLoadCurtailmentSpread",
                  selectedNetSpreadSeriesKey
                )}
                barSize={4}
                onClick={() =>
                  toggleSeriesSelection("netLoadCurtailmentSpread", setSelectedNetSpreadSeriesKey)
                }
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="w-full h-[300px] xl:h-auto xl:flex-[30] rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-base font-semibold text-gray-700 mb-2">
            {selectedGenerationType === "ALL" ? "Curtailment" : `${selectedGenerationLabel} Curtailment`}
          </h3>
          <ResponsiveContainer width="100%" height="90%">
            <LineChart data={data} margin={{ top: 8, right: 20, left: 10, bottom: 8 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
              <XAxis
                dataKey="index"
                tickFormatter={(value) => data[value]?.dateLabel ?? ""}
                ticks={xTicks}
                tick={{ fill: "#6b7280", fontSize: 12 }}
              />
              <YAxis
                tickFormatter={formatCurtailmentYAxis}
                tickCount={6}
                tick={{ fill: "#6b7280", fontSize: 12 }}
                label={{ value: "MW", angle: -90, position: "insideLeft", offset: 0 }}
              />
              <ReferenceLine y={0} stroke="#9ca3af" />
              <Tooltip
                {...MARKET_MONITOR_TOOLTIP_PROPS}
                formatter={(value: number) => formatTooltip(value)}
                labelFormatter={(value) => data[value]?.timestampLabel ?? ""}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="curtailment"
                name="Curtailment"
                stroke="#1f77d0"
                strokeWidth={2.8}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="space-y-5 xl:space-y-0 xl:flex xl:flex-col xl:gap-5 xl:h-full">
        <div className="w-full h-[360px] xl:h-auto xl:flex-[44] rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            {selectedGenerationType === "ALL"
              ? "Aggregated Solar & Wind Generation & Forecast"
              : `${selectedGenerationLabel} Generation & Forecast`}
          </h3>
          <ResponsiveContainer width="100%" height="88%">
            <LineChart data={data} margin={{ top: 8, right: 20, left: 10, bottom: 8 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
              <XAxis
                dataKey="index"
                tickFormatter={(value) => data[value]?.dateLabel ?? ""}
                ticks={xTicks}
                tick={{ fill: "#6b7280", fontSize: 12 }}
              />
              <YAxis
                tickFormatter={formatYAxis}
                tick={{ fill: "#6b7280", fontSize: 12 }}
                label={{ value: "MW", angle: -90, position: "insideLeft", offset: 0 }}
              />
              <Tooltip
                {...MARKET_MONITOR_TOOLTIP_PROPS}
                formatter={(value: number) => formatTooltip(value)}
                labelFormatter={(value) => data[value]?.timestampLabel ?? ""}
              />
              <Legend
                wrapperStyle={{ cursor: "pointer" }}
                onClick={handleAggregatedLegendClick}
                formatter={(value: string) =>
                  renderLegendLabel(value, selectedAggregatedSeriesKey, aggregatedSeriesLabelToKey)
                }
              />
              <Line
                type="monotone"
                dataKey="solarGenRealTime"
                name="Solar Gen Real Time"
                stroke={getSeriesColor("solarGenRealTime", "#f59e0b", selectedAggregatedSeriesKey)}
                strokeOpacity={getSeriesOpacity("solarGenRealTime", selectedAggregatedSeriesKey)}
                strokeWidth={selectedAggregatedSeriesKey === "solarGenRealTime" ? 3.2 : 2.4}
                dot={false}
                onClick={() =>
                  toggleSeriesSelection("solarGenRealTime", setSelectedAggregatedSeriesKey)
                }
              />
              <Line
                type="monotone"
                dataKey="solarGenForecast"
                name="Solar Gen Forecast"
                stroke={getSeriesColor("solarGenForecast", "#b45309", selectedAggregatedSeriesKey)}
                strokeOpacity={getSeriesOpacity("solarGenForecast", selectedAggregatedSeriesKey)}
                strokeWidth={selectedAggregatedSeriesKey === "solarGenForecast" ? 3.2 : 2.4}
                dot={false}
                onClick={() =>
                  toggleSeriesSelection("solarGenForecast", setSelectedAggregatedSeriesKey)
                }
              />
              <Line
                type="monotone"
                dataKey="windGenRealTime"
                name="Wind Gen Real Time"
                stroke={getSeriesColor("windGenRealTime", "#2563eb", selectedAggregatedSeriesKey)}
                strokeOpacity={getSeriesOpacity("windGenRealTime", selectedAggregatedSeriesKey)}
                strokeWidth={selectedAggregatedSeriesKey === "windGenRealTime" ? 3.2 : 2.4}
                dot={false}
                onClick={() =>
                  toggleSeriesSelection("windGenRealTime", setSelectedAggregatedSeriesKey)
                }
              />
              <Line
                type="monotone"
                dataKey="windGenForecast"
                name="Wind Gen Forecast"
                stroke={getSeriesColor("windGenForecast", "#1e3a8a", selectedAggregatedSeriesKey)}
                strokeOpacity={getSeriesOpacity("windGenForecast", selectedAggregatedSeriesKey)}
                strokeWidth={selectedAggregatedSeriesKey === "windGenForecast" ? 3.2 : 2.4}
                dot={false}
                onClick={() =>
                  toggleSeriesSelection("windGenForecast", setSelectedAggregatedSeriesKey)
                }
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="w-full xl:h-auto xl:flex-[12] rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Generation Type</h3>
          <div className="flex items-center w-full gap-3 bg-[#F7F7FF] p-2 rounded-lg">
            <button
              type="button"
              onClick={() => handleGenerationTypeSelect("SOLAR")}
              className={`${getGenerationTabClassName(selectedGenerationType === "SOLAR")} flex-1`}
            >
              Solar
            </button>
            <button
              type="button"
              onClick={() => handleGenerationTypeSelect("WIND")}
              className={`${getGenerationTabClassName(selectedGenerationType === "WIND")} flex-1`}
            >
              Wind
            </button>
          </div>
        </div>

        <div className="w-full h-[330px] xl:h-auto xl:flex-[40] rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            {selectedGenerationType === "ALL"
              ? "Solar & Wind Actual vs Forecast Spread"
              : `${selectedGenerationLabel} Actual vs Forecast Spread`}
          </h3>
          <ResponsiveContainer width="100%" height="88%">
            <BarChart data={solarWindSpreadData} margin={{ top: 8, right: 20, left: 10, bottom: 8 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
              <XAxis
                dataKey="index"
                tickFormatter={(value) => solarWindSpreadData[value]?.dateLabel ?? ""}
                ticks={solarWindSpreadTicks}
                tick={{ fill: "#6b7280", fontSize: 12 }}
              />
              <YAxis
                tickFormatter={formatYAxis}
                tick={{ fill: "#6b7280", fontSize: 12 }}
                label={{ value: "MW", angle: -90, position: "insideLeft", offset: 0 }}
              />
              <ReferenceLine y={0} stroke="#9ca3af" />
              <Tooltip
                {...MARKET_MONITOR_TOOLTIP_PROPS}
                formatter={(value: number) => formatSpreadTooltip(value)}
                labelFormatter={(value) => solarWindSpreadData[value]?.timestampLabel ?? ""}
              />
              <Legend
                wrapperStyle={{ cursor: "pointer" }}
                onClick={handleSolarWindSpreadLegendClick}
                formatter={(value: string) =>
                  renderLegendLabel(
                    value,
                    selectedSolarWindSpreadSeriesKey,
                    solarWindSpreadSeriesLabelToKey
                  )
                }
              />
              {(selectedGenerationType === "ALL" || selectedGenerationType === "SOLAR") && (
                <Bar
                  dataKey="solarSpread"
                  name="Solar"
                  fill={getSeriesColor("solarSpread", "#1e88e5", selectedSolarWindSpreadSeriesKey)}
                  fillOpacity={getSeriesOpacity("solarSpread", selectedSolarWindSpreadSeriesKey)}
                  barSize={3}
                  onClick={() =>
                    toggleSeriesSelection("solarSpread", setSelectedSolarWindSpreadSeriesKey)
                  }
                />
              )}
              {(selectedGenerationType === "ALL" || selectedGenerationType === "WIND") && (
                <Bar
                  dataKey="windSpread"
                  name="Wind"
                  fill={getSeriesColor("windSpread", "#1f2a99", selectedSolarWindSpreadSeriesKey)}
                  fillOpacity={getSeriesOpacity("windSpread", selectedSolarWindSpreadSeriesKey)}
                  barSize={3}
                  onClick={() =>
                    toggleSeriesSelection("windSpread", setSelectedSolarWindSpreadSeriesKey)
                  }
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default NetLoadVisual;
